import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";
import { CustomInput } from "../../Common/Components/Input/CustomInput";
import AdminRequestCostBearerCreate from "../Models/Request/AdminRequestCostBearerCreate";

/**
 * CostBearerCreatePanel
 *
 * Centered SlidePanel to create a cost bearer, using the same UI as the
 * "new processing" panel in ReportProceedingList (SelectOrgPanel).
 *
 * Props:
 *   isOpen     boolean
 *   onClose    () => void
 *   onCreated  () => void   called after the cost bearer is created
 */
export function CostBearerCreatePanel({ isOpen, onClose, onCreated }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const [costBearer, setCostBearer] = useState(new AdminRequestCostBearerCreate());
    const [fieldErrors, setFieldErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const schema = AdminRequestCostBearerCreate.getAttributes(null, true);

    // Reset on open/close
    useEffect(() => {
        if (!isOpen) {
            setCostBearer(new AdminRequestCostBearerCreate());
            setFieldErrors({});
            setSaving(false);
        }
    }, [isOpen]);

    const handleChange = (name, value) => {
        setCostBearer(prev => ({ ...prev, [name]: value }));
        setFieldErrors(prev => {
            const copy = { ...prev };
            delete copy[name];
            return copy;
        });
    };

    const handleBlur = async (name) => {
        try {
            await schema.validateAt(name, costBearer);
        } catch (err) {
            setFieldErrors(prev => ({ ...prev, [name]: err.message }));
        }
    };

    const checkRequired = () =>
        !!costBearer.value && costBearer.value.trim() !== "" &&
        !!costBearer.name && costBearer.name.trim() !== "";

    const handleCreate = async () => {
        if (saving || !checkRequired()) return;

        try {
            await schema.validate(costBearer, { abortEarly: false });
        } catch (err) {
            const newErrors = {};
            (err?.inner?.length ? err.inner : [err]).forEach(e => {
                if (e?.path) newErrors[e.path] = e.message;
            });
            setFieldErrors(newErrors);
            return;
        }

        const payload = new URLSearchParams();
        payload.append("name", costBearer.name ?? "");
        payload.append("value", costBearer.value ?? "");
        payload.append("showLabel", costBearer.showLabel ?? "");
        payload.append("message", costBearer.message ?? "");
        payload.append("includedInAGI", costBearer.includedInAGI ? "true" : "false");

        setSaving(true);
        await ApiCall(
            { method: "post", url: `/costbearer?${payload.toString()}` },
            (response) => {
                if (response && response.id) {
                    FlashMessage(t('validationMessage.costbearerSuccessfully'), 'flash-messageSuccess');
                    onClose();
                    if (onCreated) onCreated(response);
                } else {
                    setSaving(false);
                }
            },
            (errorMessage) => {
                setSaving(false);
                if (errorMessage.includes('validationMessage.kbarExists') || errorMessage.includes('validationMessage.nameExists')) {
                    FlashMessage(t(errorMessage), 'flash-messageError');
                } else {
                    FlashMessage(t('validationMessage.sendError'), 'flash-messageError');
                }
            }
        );
    };

    const renderFieldError = (name) => {
        const err = fieldErrors[name];
        if (!err) return null;
        return (
            <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>
                {typeof err === 'string' ? t(err) : err}
            </div>
        );
    };

    const canCreate = checkRequired() && !saving;

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={t('costBearer.create')}
            variant="center"
        >
            <div className="select-org-panel-wrapper">
                {/* KBAR */}
                <div className="mb-3">
                    <CustomInput
                        model={AdminRequestCostBearerCreate}
                        attribute="value"
                        data={costBearer.value}
                        customChange={(e) => handleChange('value', e?.target?.value ?? "")}
                        inputProps={{ id: 'value', onBlur: () => handleBlur('value') }}
                    />
                    {renderFieldError('value')}
                </div>

                {/* Name */}
                <div className="mb-3">
                    <CustomInput
                        model={AdminRequestCostBearerCreate}
                        attribute="name"
                        data={costBearer.name}
                        customChange={(e) => handleChange('name', e?.target?.value ?? "")}
                        inputProps={{ id: 'name', onBlur: () => handleBlur('name') }}
                    />
                    {renderFieldError('name')}
                </div>

                {/* Message */}
                <div className="mb-3">
                    <label htmlFor="costBearerMessage" className="select-org-panel-label">
                        {t('costBearer.message')}
                    </label>
                    <textarea
                        id="costBearerMessage"
                        className="form-control"
                        value={costBearer.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        onBlur={() => handleBlur('message')}
                        style={{ resize: "vertical", minHeight: "80px", height: "auto" }}
                        rows={3}
                    />
                    {renderFieldError('message')}
                </div>

                {/* Included in AGI */}
                <div className="rpv2-multiday-row mb-4">
                    <input
                        type="checkbox"
                        id="costBearerIncludedInAGI"
                        className="rpv2-multiday-checkbox"
                        checked={!!costBearer.includedInAGI}
                        onChange={(e) => handleChange('includedInAGI', e.target.checked)}
                    />
                    <label htmlFor="costBearerIncludedInAGI" className="rpv2-multiday-label">
                        {t('costBearer.includedInAGILabel')}
                    </label>
                </div>

                {/* Buttons */}
                <div className="select-org-panel-buttons">
                    <button onClick={onClose} className="select-org-panel-btn-cancel">
                        {t('button.cancel') || "Avbryt"}
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={!canCreate}
                        className={`select-org-panel-btn-confirm ${canCreate ? "enabled" : "disabled"}`}
                    >
                        {saving
                            ? <Spinner size="sm" />
                            : (t('button.create') || "Skapa")}
                    </button>
                </div>
            </div>
        </SlidePanel>
    );
}
